/**
 * HRSection - Human Resources section for lukhas.team staff portal
 *
 * Features:
 * - Time off requests
 * - Leave balance tracking
 * - Company announcements
 * - Employee directory
 * - Quick HR actions
 */

import React, { useState } from 'react'
import {
  Calendar,
  Clock,
  Users,
  FileText,
  Megaphone,
  ChevronRight,
  Plus,
  CheckCircle,
  XCircle,
  AlertCircle,
  Briefcase,
  Heart,
  Plane,
  GraduationCap,
  Baby
} from 'lucide-react'

interface TimeOffRequest {
  id: string
  type: 'vacation' | 'sick' | 'personal' | 'parental' | 'training'
  startDate: string
  endDate: string
  status: 'pending' | 'approved' | 'denied'
  days: number
  reason?: string
}

interface LeaveBalance {
  type: string
  icon: React.ElementType
  total: number
  used: number
  pending: number
  color: string
}

interface Announcement {
  id: string
  title: string
  content: string
  date: string
  priority: 'normal' | 'important' | 'urgent'
  author: string
}

interface HRSectionProps {
  className?: string
}

const demoLeaveBalances: LeaveBalance[] = [
  { type: 'Vacation', icon: Plane, total: 20, used: 8, pending: 2, color: 'text-blue-400' },
  { type: 'Sick Leave', icon: Heart, total: 10, used: 2, pending: 0, color: 'text-red-400' },
  { type: 'Personal', icon: Clock, total: 5, used: 1, pending: 0, color: 'text-purple-400' },
  { type: 'Training', icon: GraduationCap, total: 5, used: 3, pending: 1, color: 'text-emerald-400' },
]

const demoRequests: TimeOffRequest[] = [
  { id: 'req_001', type: 'vacation', startDate: '2024-12-20', endDate: '2024-12-27', status: 'approved', days: 5, reason: 'Holiday travel' },
  { id: 'req_002', type: 'training', startDate: '2024-12-05', endDate: '2024-12-05', status: 'pending', days: 1, reason: 'Cloud certification exam' },
  { id: 'req_003', type: 'sick', startDate: '2024-11-15', endDate: '2024-11-15', status: 'approved', days: 1 },
]

const demoAnnouncements: Announcement[] = [
  {
    id: 'ann_001',
    title: 'Q4 All-Hands Meeting',
    content: 'Join us for our quarterly all-hands meeting on December 15th at 2pm PT. We will be discussing the roadmap for 2025.',
    date: '2024-11-28',
    priority: 'important',
    author: 'Dr. Maya Patel'
  },
  {
    id: 'ann_002',
    title: 'Office Holiday Schedule',
    content: 'The office will be closed December 24-26 and January 1st. Please plan accordingly.',
    date: '2024-11-25',
    priority: 'normal',
    author: 'HR Team'
  },
  {
    id: 'ann_003',
    title: 'New Benefits Enrollment',
    content: 'Open enrollment for 2025 benefits is now open. Please review and update your selections by December 10th.',
    date: '2024-11-20',
    priority: 'urgent',
    author: 'Benefits Admin'
  },
]

export default function HRSection({ className = '' }: HRSectionProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'requests' | 'announcements'>('overview')
  const [showRequestModal, setShowRequestModal] = useState(false)

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <span className="flex items-center space-x-1 px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded-full">
            <CheckCircle className="w-3 h-3" />
            <span>Approved</span>
          </span>
        )
      case 'denied':
        return (
          <span className="flex items-center space-x-1 px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded-full">
            <XCircle className="w-3 h-3" />
            <span>Denied</span>
          </span>
        )
      default:
        return (
          <span className="flex items-center space-x-1 px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-400 rounded-full">
            <AlertCircle className="w-3 h-3" />
            <span>Pending</span>
          </span>
        )
    }
  }

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded-full">Urgent</span>
      case 'important':
        return <span className="px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-400 rounded-full">Important</span>
      default:
        return null
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'vacation':
        return <Plane className="w-4 h-4 text-blue-400" />
      case 'sick':
        return <Heart className="w-4 h-4 text-red-400" />
      case 'personal':
        return <Clock className="w-4 h-4 text-purple-400" />
      case 'parental':
        return <Baby className="w-4 h-4 text-pink-400" />
      case 'training':
        return <GraduationCap className="w-4 h-4 text-emerald-400" />
      default:
        return <Calendar className="w-4 h-4 text-gray-400" />
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  return (
    <div className={`bg-gray-900/80 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-700/50 bg-gray-800/40">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Briefcase className="w-5 h-5 text-lambda-gold" />
            <h3 className="text-lg font-medium text-white">HR Center</h3>
          </div>
          <button
            onClick={() => setShowRequestModal(true)}
            className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-lambda-gold/20 text-lambda-gold border border-lambda-gold/30 rounded-lg hover:bg-lambda-gold/30 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Request Time Off</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center space-x-4 mt-4">
          {[
            { id: 'overview', label: 'Overview', icon: Calendar },
            { id: 'requests', label: 'My Requests', icon: FileText },
            { id: 'announcements', label: 'Announcements', icon: Megaphone },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex items-center space-x-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-lambda-gold/20 text-lambda-gold'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Leave Balances */}
            <div>
              <h4 className="text-sm font-medium text-gray-400 mb-3">Leave Balances</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {demoLeaveBalances.map((balance) => {
                  const available = balance.total - balance.used - balance.pending
                  const usedPercent = (balance.used / balance.total) * 100
                  const pendingPercent = (balance.pending / balance.total) * 100

                  return (
                    <div
                      key={balance.type}
                      className="bg-gray-800/30 rounded-lg p-4 border border-gray-700/30"
                    >
                      <div className="flex items-center space-x-2 mb-3">
                        <balance.icon className={`w-5 h-5 ${balance.color}`} />
                        <span className="text-sm text-white font-medium">{balance.type}</span>
                      </div>
                      <div className="text-2xl font-bold text-white mb-1">
                        {available} <span className="text-sm font-normal text-gray-500">days</span>
                      </div>
                      <div className="text-xs text-gray-500 mb-2">
                        {balance.used} used {balance.pending > 0 && ` ${balance.pending} pending`}
                      </div>
                      <div className="h-1.5 bg-gray-700/50 rounded-full overflow-hidden">
                        <div className="h-full flex">
                          <div
                            className={`${balance.color.replace('text-', 'bg-')}`}
                            style={{ width: `${usedPercent}%` }}
                          />
                          {balance.pending > 0 && (
                            <div
                              className={`${balance.color.replace('text-', 'bg-')} opacity-50`}
                              style={{ width: `${pendingPercent}%` }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <h4 className="text-sm font-medium text-gray-400 mb-3">Quick Actions</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'View Payslips', icon: FileText },
                  { label: 'Update Emergency Contact', icon: Users },
                  { label: 'Benefits Info', icon: Heart },
                  { label: 'Company Directory', icon: Users },
                ].map((action) => (
                  <button
                    key={action.label}
                    className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg border border-gray-700/30 hover:border-gray-600/50 transition-colors group"
                  >
                    <div className="flex items-center space-x-2">
                      <action.icon className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-300">{action.label}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-gray-400 transition-colors" />
                  </button>
                ))}
              </div>
            </div>

            {/* Recent Announcements Preview */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-400">Recent Announcements</h4>
                <button
                  onClick={() => setActiveTab('announcements')}
                  className="text-xs text-lambda-gold hover:underline"
                >
                  View all
                </button>
              </div>
              <div className="space-y-2">
                {demoAnnouncements.slice(0, 2).map((announcement) => (
                  <div
                    key={announcement.id}
                    className="p-3 bg-gray-800/30 rounded-lg border border-gray-700/30"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-white">{announcement.title}</span>
                      {getPriorityBadge(announcement.priority)}
                    </div>
                    <p className="text-xs text-gray-500">{formatDate(announcement.date)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'requests' && (
          <div className="space-y-3">
            {demoRequests.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No time off requests yet</p>
              </div>
            ) : (
              demoRequests.map((request) => (
                <div
                  key={request.id}
                  className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg border border-gray-700/30"
                >
                  <div className="flex items-center space-x-4">
                    <div className="p-2 bg-gray-700/30 rounded-lg">
                      {getTypeIcon(request.type)}
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-white capitalize">{request.type}</span>
                        {getStatusBadge(request.status)}
                      </div>
                      <p className="text-sm text-gray-400">
                        {formatDate(request.startDate)}
                        {request.startDate !== request.endDate && ` - ${formatDate(request.endDate)}`}
                      </p>
                      {request.reason && (
                        <p className="text-xs text-gray-500 mt-1">{request.reason}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-semibold text-white">{request.days}</span>
                    <span className="text-xs text-gray-500 ml-1">day{request.days > 1 ? 's' : ''}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'announcements' && (
          <div className="space-y-4">
            {demoAnnouncements.map((announcement) => (
              <div
                key={announcement.id}
                className="p-4 bg-gray-800/30 rounded-lg border border-gray-700/30"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Megaphone className={`w-4 h-4 ${
                      announcement.priority === 'urgent' ? 'text-red-400' :
                      announcement.priority === 'important' ? 'text-yellow-400' :
                      'text-gray-400'
                    }`} />
                    <span className="text-sm font-medium text-white">{announcement.title}</span>
                    {getPriorityBadge(announcement.priority)}
                  </div>
                  <span className="text-xs text-gray-500">{formatDate(announcement.date)}</span>
                </div>
                <p className="text-sm text-gray-300 mb-2">{announcement.content}</p>
                <p className="text-xs text-gray-500">Posted by {announcement.author}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Request Modal Placeholder */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-xl border border-gray-700/50 w-full max-w-md p-6 m-4">
            <h3 className="text-lg font-medium text-white mb-4">Request Time Off</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 block mb-1">Leave Type</label>
                <select className="w-full bg-gray-800/50 border border-gray-700/50 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-lambda-gold/50">
                  <option value="vacation">Vacation</option>
                  <option value="sick">Sick Leave</option>
                  <option value="personal">Personal</option>
                  <option value="training">Training</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Start Date</label>
                  <input
                    type="date"
                    className="w-full bg-gray-800/50 border border-gray-700/50 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-lambda-gold/50"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">End Date</label>
                  <input
                    type="date"
                    className="w-full bg-gray-800/50 border border-gray-700/50 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-lambda-gold/50"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Reason (optional)</label>
                <textarea
                  rows={3}
                  className="w-full bg-gray-800/50 border border-gray-700/50 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-lambda-gold/50 resize-none"
                  placeholder="Brief description..."
                />
              </div>
            </div>
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowRequestModal(false)}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowRequestModal(false)}
                className="px-4 py-2 text-sm bg-lambda-gold/20 text-lambda-gold border border-lambda-gold/30 rounded-lg hover:bg-lambda-gold/30 transition-colors"
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
